import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAlert } from '../../providers/AlertProvider';
import logger from "../../utils/logger";

/**
 * ProductLabelModal.jsx
 * 
 * Specialized modal for printing product labels with QR codes
 * Designed for Zebra printer label dimensions (4" x 6" scale)
 */
export default function ProductLabelModal({ 
    qrUrl, 
    productData = null,
    onClose 
}) {
    const { showAlert } = useAlert();
    const [quantity, setQuantity] = useState(1);
    const [isPrinting, setIsPrinting] = useState(false);

    // Check if we're on HTTPS and have Web Serial API support
    const canUseWebSerial = 'serial' in navigator && window.location.protocol === 'https:';
    const isHttpTesting = window.location.protocol === 'http:';

    if (!qrUrl || !productData) return null;

    // Zebra label dimensions (scaled for preview) - 4" x 2"
    const labelWidth = 400; // 4 inches scaled
    const labelHeight = 200; // 2 inches scaled
    const qrSize = 160; // QR code size

    const handlePrint = async () => {
        setIsPrinting(true);
        try {
            // Check browser compatibility first
            if (!canUseWebSerial) {
                showAlert({
                    message: 'Direct USB printing requires HTTPS connection. Use the "Download ZPL" button instead for HTTP testing.',
                    type: 'warning',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                return;
            }

            // Generate ZPL (Zebra Programming Language) code
            const zplCode = generateZPL(productData, quantity);
            
            // Send to Zebra printer via USB
            await sendToZebraPrinter(zplCode);
            
            // Show success message
            showAlert({
                message: `Successfully sent ${quantity} label(s) to printer!`,
                type: 'success',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            onClose();
        } catch (error) {
            logger.error('Print error:', error);
            
            // Provide helpful error messages
            let errorMessage = 'Failed to print labels. ';
            if (error.message.includes('not supported')) {
                errorMessage += 'Please use Chrome or Edge browser with HTTPS connection.';
            } else if (error.message.includes('Permission denied')) {
                errorMessage += 'Please allow printer access when the browser asks for permission.';
            } else if (error.message.includes('No compatible')) {
                errorMessage += 'Please check that your Zebra printer is connected via USB and powered on.';
            } else if (error.message.includes('communication failed')) {
                errorMessage += 'Check if the printer is ready and not busy with another job.';
            } else {
                errorMessage += error.message;
            }
            
            showAlert({
                message: errorMessage,
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsPrinting(false);
        }
    };

    const handleDownloadZPL = () => {
        try {
            const zplCode = generateZPL(productData, quantity);
            
            // Create a blob with the ZPL content
            const blob = new Blob([zplCode], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            
            // Create a temporary download link
            const link = document.createElement('a');
            link.href = url;
            link.download = `${productData.product_code}_label.zpl`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up the URL
            URL.revokeObjectURL(url);
            
            showAlert({
                message: 'ZPL file downloaded! You can now send this file to your Zebra printer using Zebra Setup Utilities or copy the printer manually.',
                type: 'success',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } catch (error) {
            logger.error('Download error:', error);
            showAlert({
                message: 'Failed to generate ZPL file.',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        }
    };

    const generateZPL = (product, qty) => {
        // Create QR data with actual product information instead of Supabase URL
        const qrData = JSON.stringify({
            type: "product",
            product_code: product.product_code,
            product_name: product.product_name,
            batch_no: product.batch_no || 'N/A',
            customer_id: product.customer_id,
            id: product.id
        });
        
        // Generate ZPL code for Zebra printer using your custom format
        // Based on your zebra_setup.zpl format with text moved 5mm to the right
        const zpl = `^XA
^MMT
^PW799
^LL343
^LT72
^LS0

^FT60,20^BQN,2,5^FH\\^FDMA,${qrData}^FS

^FT370,50^A0N,28,28^FH\\^FDProduct Code:^FS
^FT370,85^A0N,32,32^FH\\^FD${product.product_code}^FS

^FT370,140^A0N,24,24^FH\\^FDProduct Name:^FS
^FT370,165^A0N,28,28^FH\\^FD${product.product_name}^FS

^FT370,215^A0N,24,24^FH\\^FDBatch: ${product.batch_no || 'N/A'}^FS

^PQ${qty},0,1,Y
^XZ`;
        
        logger.log('Generated ZPL with product data QR:', zpl);
        return zpl;
    };

    const sendToZebraPrinter = async (zplCode) => {
        // Check if browser supports Web Serial API
        if (!('serial' in navigator)) {
            throw new Error('Web Serial API not supported. Please use Chrome/Edge browser with HTTPS.');
        }

        try {
            // Request access to serial port (Zebra printer)
            const port = await navigator.serial.requestPort({
                filters: [
                    // Zebra vendor ID filters
                    { usbVendorId: 0x0A5F }, // Zebra Technologies Corp
                    { usbVendorId: 0x04B4 }, // Cypress (some Zebra models)
                ]
            });

            // Open connection with appropriate settings for Zebra printers
            await port.open({ 
                baudRate: 9600,  // Standard for most Zebra printers
                dataBits: 8,
                stopBits: 1,
                parity: 'none',
                flowControl: 'none'
            });
            
            const writer = port.writable.getWriter();
            const encoder = new TextEncoder();
            
            // Send ZPL code to printer
            await writer.write(encoder.encode(zplCode));
            
            // Clean up
            await writer.close();
            await port.close();
            
            logger.log('Successfully sent ZPL code to printer');
            
        } catch (error) {
            logger.error('Printer communication error:', error);
            
            // Provide specific error messages
            if (error.name === 'NotFoundError') {
                throw new Error('No compatible Zebra printer found. Please check USB connection.');
            } else if (error.name === 'NotAllowedError') {
                throw new Error('Permission denied. Please allow printer access when prompted.');
            } else if (error.name === 'NetworkError') {
                throw new Error('Printer communication failed. Check if printer is ready and not busy.');
            } else {
                throw new Error(`Printer error: ${error.message}`);
            }
        }
    };

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2000,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: "white",
                    padding: "2rem",
                    borderRadius: "12px",
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
                    maxWidth: "800px",
                    width: "90%",
                    position: "relative",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        background: "none",
                        border: "none",
                        fontSize: "1.5rem",
                        cursor: "pointer",
                        color: "#666",
                        padding: "5px",
                        lineHeight: 1,
                    }}
                    title="Close"
                >
                    ×
                </button>

                {/* Title */}
                <h3 style={{
                    margin: "0 0 1.5rem 0",
                    color: "#333",
                    fontSize: "1.3rem",
                    fontWeight: 600,
                    textAlign: "center",
                }}>
                    Product Label Preview
                </h3>

                {/* Label Preview */}
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "2rem",
                }}>
                    <div
                        style={{
                            width: `${labelWidth}px`,
                            height: `${labelHeight}px`,
                            border: "2px solid #333",
                            backgroundColor: "#fff",
                            display: "flex",
                            alignItems: "center",
                            padding: "1rem",
                            borderRadius: "4px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                    >
                        {/* QR Code Section */}
                        <div style={{
                            width: `${qrSize}px`,
                            height: `${qrSize}px`,
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f9f9f9",
                            marginRight: "1rem",
                        }}>
                            <img 
                                src={qrUrl} 
                                alt="Product QR Code"
                                style={{ 
                                    maxWidth: "90%",
                                    maxHeight: "90%",
                                    width: "auto",
                                    height: "auto",
                                }}
                            />
                        </div>

                        {/* Product Information Section */}
                        <div style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            paddingLeft: "0.5rem",
                        }}>
                            <div style={{ marginBottom: "0.5rem" }}>
                                <div style={{
                                    fontSize: "0.9rem",
                                    fontWeight: "600",
                                    color: "#333",
                                    marginBottom: "0.2rem",
                                }}>
                                    Product Code:
                                </div>
                                <div style={{
                                    fontSize: "1.1rem",
                                    fontWeight: "500",
                                    color: "#000",
                                    fontFamily: "monospace",
                                    wordBreak: "break-all",
                                }}>
                                    {productData.product_code}
                                </div>
                            </div>

                            <div style={{ marginBottom: "0.5rem" }}>
                                <div style={{
                                    fontSize: "0.9rem",
                                    fontWeight: "600",
                                    color: "#333",
                                    marginBottom: "0.2rem",
                                }}>
                                    Product Name:
                                </div>
                                <div style={{
                                    fontSize: "1rem",
                                    fontWeight: "400",
                                    color: "#000",
                                    lineHeight: "1.2",
                                }}>
                                    {productData.product_name}
                                </div>
                            </div>

                            <div>
                                <div style={{
                                    fontSize: "0.9rem",
                                    fontWeight: "600",
                                    color: "#333",
                                    marginBottom: "0.2rem",
                                }}>
                                    Batch Number:
                                </div>
                                <div style={{
                                    fontSize: "1rem",
                                    fontWeight: "400",
                                    color: "#666",
                                    fontFamily: "monospace",
                                }}>
                                    {productData.batch_no || 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Button with Quantity */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "1rem",
                    marginBottom: "1rem",
                }}>
                    <label style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        fontSize: "1rem",
                        fontWeight: "500",
                    }}>
                        Quantity:
                        <input
                            type="number"
                            min="1"
                            max="100"
                            value={quantity}
                            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                            style={{
                                padding: "0.5rem",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                width: "80px",
                                textAlign: "center",
                                fontSize: "1rem",
                            }}
                        />
                    </label>
                    
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button
                            onClick={handleDownloadZPL}
                            style={{
                                padding: "0.75rem 1.5rem",
                                backgroundColor: "#28a745",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "1rem",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                            }}
                        >
                            <span>📥</span>
                            Download ZPL
                        </button>

                        {canUseWebSerial && (
                        <button
                            onClick={handlePrint}
                            disabled={isPrinting}
                            style={{
                                padding: "0.75rem 1.5rem",
                                backgroundColor: isPrinting ? "#6c757d" : "#007bff",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: isPrinting ? "not-allowed" : "pointer",
                                fontSize: "1rem",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                            }}
                        >
                            {isPrinting ? (
                                <>
                                    <span>🖨️</span>
                                    Printing...
                                </>
                            ) : (
                                <>
                                    <span>🖨️</span>
                                    Print via USB
                                </>
                            )}
                        </button>
                        )}
                        
                        {isHttpTesting && (
                            <div style={{
                                padding: "0.75rem 1rem",
                                backgroundColor: "#ffc107",
                                color: "#000",
                                border: "none",
                                borderRadius: "6px",
                                fontSize: "0.9rem",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                            }}>
                                ⚠️ HTTP Testing Mode
                            </div>
                        )}
                    </div>
                </div>

                {/* Instructions */}
                <div style={{
                    marginTop: "1.5rem",
                    padding: "1rem",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                    borderRadius: "6px",
                    fontSize: "0.9rem",
                    color: "#495057",
                }}>
                    {canUseWebSerial ? (
                        <>
                            <strong>🖨️ Direct USB Printing (HTTPS):</strong>
                            <ul style={{ margin: "0.5rem 0 0 1rem", padding: 0 }}>
                                <li>Connect Zebra printer via USB cable</li>
                                <li>Ensure printer is powered on and ready</li>
                                <li>Click Print and allow browser permission when prompted</li>
                            </ul>
                        </>
                    ) : (
                        <>
                            <strong>📥 ZPL File Download {isHttpTesting ? '(HTTP Testing Mode)' : ''}:</strong>
                            <ul style={{ margin: "0.5rem 0 0 1rem", padding: 0 }}>
                                <li>Download the ZPL file to your computer</li>
                                <li>Send to printer using Zebra Setup Utilities</li>
                                <li>Or copy file content to printer spooler</li>
                                {isHttpTesting && <li><strong>For direct printing: Use HTTPS with Chrome/Edge</strong></li>}
                            </ul>
                        </>
                    )}
                    
                    <div style={{ marginTop: "0.8rem", fontSize: "0.85rem", color: "#6c757d" }}>
                        <strong>💡 Alternative Methods:</strong><br/>
                        • Windows: <code>copy filename.zpl &gt; LPT1:</code> or <code>copy filename.zpl &gt; COM1:</code><br/>
                        • Linux: <code>cat filename.zpl &gt; /dev/usb/lp0</code><br/>
                        • Use Zebra Setup Utilities for GUI file sending
                    </div>
                </div>
            </div>
        </div>
    );
}

ProductLabelModal.propTypes = {
    qrUrl: PropTypes.string.isRequired,
    productData: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
};
